import { handleActions, combineActions } from 'redux-actions';
import { ROUTINE_PROMISE_ACTION } from 'redux-saga-routines';
import { combineReducers } from 'redux';
import omit from 'lodash/omit';
import merge from 'lodash/merge';
import orderBy from 'lodash/orderBy';
import isEmpty from 'lodash/isEmpty';
import getPath from 'lodash/get';
import * as types from '../actions/types';
import {
  fetchManagedSoundcastEpisodes,
  updateSoundcastEpisodesOrder,
  deleteManagedSoundcastEpisodes,
  bulkActionEpisodes,
} from '../routines';

const byId = handleActions(
  {
    [combineActions(
      types.RECEIVE_MANAGED_SOUNDCAST_EPISODES,
      types.ADD_UPDATE_MANAGED_SOUNDCAST_EPISODE,
      updateSoundcastEpisodesOrder.SUCCESS,
      bulkActionEpisodes.SUCCESS
    )]: (state, { payload: { episodes, overwrite = false } }) => {
      if (overwrite) {
        const nstate = Object.keys(episodes).reduce((prev, episodeId) => {
          prev[episodeId] = episodes[episodeId];
          return prev;
        }, {});

        return {
          ...state,
          ...nstate,
        };
      }

      return merge({}, state, episodes);
    },
    [deleteManagedSoundcastEpisodes.SUCCESS]: (state, { payload: { episodes } }) =>
      omit(state, episodes),
    [types.SIGNOUT]: () => ({}),
  },
  {}
);

const bySoundcast = handleActions(
  {
    [types.RECEIVE_MANAGED_SOUNDCAST_EPISODES]: (state, { payload: { soundcastId, episodes } }) => {
      const newEpisodeIds = Object.keys(episodes);
      const oldEpisodeIds = state[soundcastId] || [];
      return merge({}, state, { [soundcastId]: [...oldEpisodeIds, ...newEpisodeIds] });
    },
    [types.ADD_UPDATE_MANAGED_SOUNDCAST_EPISODE]: (
      state,
      { payload: { soundcastId, episodes } }
    ) => {
      const oldState = soundcastId in state ? state[soundcastId] : [];

      // since update can be here, make sure to only unshift
      // episodeIds that are not existed yet
      const newOrderedEpisodeKeys = orderBy(
        Object.keys(episodes),
        episodeId => getPath(episodes[episodeId], 'index', 0),
        ['desc']
      ).filter(episodeId => !oldState.includes(episodeId));

      // get oldState and push new keys to top of array
      const newState = [...oldState];
      newState.unshift(...newOrderedEpisodeKeys);

      return {
        ...state,
        [soundcastId]: newState,
      };
    },
    [updateSoundcastEpisodesOrder.SUCCESS]: (state, { payload: { soundcastId, episodes } }) => {
      const newOrderedEpisodeKeys = orderBy(
        Object.keys(episodes),
        episodeId => getPath(episodes[episodeId], 'index', 0),
        ['desc']
      );

      return {
        ...state,
        [soundcastId]: newOrderedEpisodeKeys,
      };
    },
    [deleteManagedSoundcastEpisodes.SUCCESS]: (state, { payload: { soundcastId, episodes } }) => {
      const newState = state[soundcastId].filter(episodeId => !episodes.includes(episodeId));
      return {
        ...state,
        [soundcastId]: newState,
      };
    },
  },
  {}
);

const byTotal = handleActions(
  {
    [types.RECEIVE_MANAGED_SOUNDCAST_EPISODES]: (state, { payload: { soundcastId, total } }) =>
      merge({}, state, { [soundcastId]: total }),
    [deleteManagedSoundcastEpisodes.SUCCESS]: (state, { payload: { soundcastId, episodes } }) => {
      const total = state[soundcastId];
      return merge({}, state, { [soundcastId]: total - episodes.length });
    },
  },
  {}
);

const busy = handleActions(
  {
    [types.RECEIVE_MANAGED_SOUNDCAST_EPISODES]: (state, { payload: { soundcastId } }) =>
      merge({}, state, { [soundcastId]: false }),
    [combineActions(
      updateSoundcastEpisodesOrder.FULFILL,
      deleteManagedSoundcastEpisodes.FULFILL,
      bulkActionEpisodes.FULFILL
    )]: (state, { payload: { soundcastId } }) => merge({}, state, { [soundcastId]: false }),
    [ROUTINE_PROMISE_ACTION]: (state, { payload: { soundcast = {}, silent = false } }) => {
      const { soundcastId = '' } = soundcast;
      if (isEmpty(soundcastId)) {
        return state;
      }

      return merge({}, state, { [soundcastId]: silent ? false : true });
    },
  },
  {}
);

const operation = handleActions(
  {
    [combineActions(
      fetchManagedSoundcastEpisodes.TRIGGER,
      deleteManagedSoundcastEpisodes.TRIGGER,
      updateSoundcastEpisodesOrder.TRIGGER,
      bulkActionEpisodes.TRIGGER
    )]: (_, { payload: { operation = '' } }) => operation,
    [combineActions(
      fetchManagedSoundcastEpisodes.FULFILL,
      deleteManagedSoundcastEpisodes.FULFILL,
      updateSoundcastEpisodesOrder.FULFILL,
      bulkActionEpisodes.FULFILL
    )]: () => '',
  },
  ''
);

const error = handleActions(
  {
    [combineActions(
      updateSoundcastEpisodesOrder.FAILURE,
      deleteManagedSoundcastEpisodes.FAILURE,
      bulkActionEpisodes.FAILURE
    )]: (state, { payload: { soundcastId, error } }) => merge({}, state, { [soundcastId]: error }),
  },
  {}
);

export default combineReducers({ byId, bySoundcast, byTotal, busy, operation, error });
